import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNav } from '../../store/nav';
import { useProfile } from '../../store/profile';
import { useMe } from '../../hooks/useMe';
import { startEmail, verifyEmail } from '../../lib/me';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { FigIcon } from '../FigIcon';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LEN = 6;
const VALID_CODE = '123456';

/** Circular icon badge shared by both steps (white bg, 80px, blue glyph). */
function Badge({ icon }: { icon: 'pfSms' | 'pfEmail' }) {
  return (
    <span className="grid size-20 place-items-center rounded-full bg-card">
      <FigIcon name={icon} size={36} className="text-accent" />
    </span>
  );
}

/** Email flow — Figma section 2:7491. Step 1: enter email + Continue.
 *  Step 2: 6-digit code sent to the email; auto-submits on completion. */
export function EmailScreen() {
  const { t } = useTranslation();
  const pop = useNav((s) => s.pop);
  const setEmailStored = useProfile((s) => s.setEmail);
  const { token, refresh } = useMe();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const codeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const emailValid = EMAIL_RE.test(email.trim());

  const submitEmail = () => {
    if (!emailValid) return;
    haptic();
    setCode('');
    setError(false);
    setVerifying(false);
    // Real backend (Telegram): ask the server to email a code. Dev/browser: skip
    // straight to the code step (mock 123456).
    if (token) startEmail(token, email.trim()).catch(() => {});
    setStep('code');
  };

  // Auto-submit once all digits are entered. With the backend the server checks
  // the code; in dev/browser only 123456 succeeds. Wrong → red error state.
  useEffect(() => {
    if (step !== 'code' || code.length !== CODE_LEN || verifying) return;
    if (token) {
      let alive = true;
      setVerifying(true);
      verifyEmail(token, code)
        .then(() => {
          if (!alive) return;
          haptic();
          setEmailStored(email.trim());
          refresh();
          setTimeout(pop, 450); // keep the spinner until the screen pops
        })
        .catch(() => {
          if (!alive) return;
          haptic();
          setVerifying(false);
          setError(true);
        });
      return () => {
        alive = false;
      };
    }
    if (code === VALID_CODE) {
      haptic();
      setEmailStored(email.trim());
      const timer = setTimeout(pop, 450);
      return () => clearTimeout(timer);
    }
    haptic();
    setError(true);
  }, [code, step, email, pop, token, refresh, setEmailStored, verifying]);

  // Native Telegram BackButton: on the code step go back to email entry;
  // on the email step it pops the route.
  useBackHandler(step === 'code', () => {
    setVerifying(false);
    setStep('email');
  });

  return (
    <div className="push-enter flex min-h-full flex-col bg-surface pt-6">
      {step === 'email' ? (
        <div className="flex flex-1 flex-col px-4 pb-40 pt-6">
          <div className="flex flex-col items-center gap-4">
            <Badge icon="pfSms" />
            <h1 className="text-[24px] font-medium text-ink">{t('emailFlow.enterTitle')}</h1>
            <p className="w-[277px] text-center text-[15px] leading-[1.44] text-muted">
              {t('emailFlow.subtitle')}
            </p>
          </div>

          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitEmail()}
            placeholder={t('emailFlow.placeholder')}
            className="mt-6 h-[60px] w-full rounded-[20px] bg-card px-4 text-[16px] text-ink outline-none placeholder:text-muted"
          />

          <button
            onClick={submitEmail}
            disabled={!emailValid}
            className={`mt-8 flex h-14 w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold transition-colors ${
              emailValid ? 'text-white active:opacity-90' : 'text-white/[0.44]'
            }`}
          >
            {t('common.continue')}
          </button>
        </div>
      ) : (
        <div className="flex flex-1 flex-col px-4 pb-40 pt-6">
          <div className="flex flex-col items-center gap-4">
            <Badge icon="pfEmail" />
            <h1 className="text-[24px] font-medium text-ink">{t('emailFlow.codeTitle')}</h1>
            <p className="w-[332px] text-center text-[15px] leading-[1.44] text-muted">
              {t('emailFlow.codeSubtitle', { email: email.trim() })}
            </p>
          </div>

          {/* OTP boxes with a transparent full-size input overlaid so tapping
              anywhere on the row focuses it and opens the keyboard. */}
          <div className="relative mt-6">
            <div className={`flex w-full gap-[10px] transition-opacity ${verifying ? 'opacity-40' : ''}`}>
              {Array.from({ length: CODE_LEN }).map((_, i) => {
                const filled = i < code.length;
                const active = i === code.length;
                const border = error
                  ? 'border-negative'
                  : filled
                    ? 'border-accent'
                    : active
                      ? 'border-[rgba(33,149,241,0.5)]'
                      : 'border-transparent';
                return (
                  <span
                    key={i}
                    className={`flex h-[60px] flex-1 items-center justify-center rounded-[20px] border bg-card text-[24px] ${error ? 'text-negative' : 'text-ink'} ${border}`}
                  >
                    {code[i] ?? ''}
                  </span>
                );
              })}
            </div>
            <input
              ref={codeRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LEN}
              value={code}
              disabled={verifying}
              onChange={(e) => {
                setError(false);
                setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LEN));
              }}
              className="absolute inset-0 h-full w-full bg-transparent text-transparent caret-transparent outline-none disabled:pointer-events-none"
              aria-label={t('emailFlow.codeTitle')}
            />
            {verifying && (
              <span className="absolute inset-0 grid place-items-center">
                <span className="size-7 animate-spin rounded-full border-[3px] border-border border-t-accent" />
              </span>
            )}
          </div>

          {error && (
            <p className="mt-3 text-center text-[14px] text-negative">{t('emailFlow.wrongCode')}</p>
          )}
        </div>
      )}
    </div>
  );
}
